import type { ApolloError } from '@apollo/client'
import moment from 'moment'
import type React from 'react'
import { useState } from 'react'
import styled from 'styled-components'

import DateComponent from '@/components/DateComponent'
import TooltipIcon from '@/components/IconGroup/TooltipIcon'
import type { DataRequestRecommendation } from '@/types/Recommendation'

import CancelRequestDialog from './CancelRequest'

const Request = styled.div`
  border: 1px solid #34bebd;
  margin-bottom: 5px;
  height: fit-content;
  border-radius: 5px;

  &:hover {
    cursor: pointer;
  }

  h2 {
    color: #34bebd;
  }

  li {
    list-style: square;
    padding: 5px;
  }
`

const Icon = styled.img`
  margin-top: 8px;
  width: 12px;
`

const EmptyMessage = styled.div`
  margin: 20px 0;
`

const CollapsedContainer = styled.div`
  padding: 10px;

  &:hover {
    background: #f2f1f1;
    cursor: pointer;
  }
`

const ExpandedContainer = styled.div`
  padding: 10px;
`

interface Props {
  recommendations: DataRequestRecommendation[]
  onCancelRequest: (
    recommendation: DataRequestRecommendation,
    notifyApplicant: boolean,
  ) => Promise<void>
  cancelRequestLoading: boolean
  cancelRequestError?: ApolloError
}

const ActiveRequests: React.FC<Props> = ({
  recommendations,
  onCancelRequest,
  cancelRequestLoading,
  cancelRequestError,
}) => {
  const [expandedRecommendations, setExpandedRecommendations] = useState<
    DataRequestRecommendation[]
  >([])

  function handleOnClick(recommendation: DataRequestRecommendation) {
    setExpandedRecommendations(prevExpandedRecommendations => {
      const isExpanded = prevExpandedRecommendations.includes(recommendation)
      if (isExpanded) {
        return prevExpandedRecommendations.filter(item => item !== recommendation)
      }

      return [...prevExpandedRecommendations, recommendation]
    })
  }

  return (
    <>
      {recommendations.map(recommendation => (
        <Request key={recommendation.id}>
          <CollapsedContainer onClick={() => handleOnClick(recommendation)}>
            <div className="side-by-side-flex">
              <div>
                <strong>Author: </strong>
                {recommendation.author}
              </div>
              <div>
                <strong>Created: </strong>
                <DateComponent dateUTC={recommendation.createdAt!} />
              </div>
              <TooltipIcon
                icon={
                  <Icon src="/assets/svg/placeholder.svg" alt="an information icon" />
                }
                tooltipMessage="View Details"
              />
            </div>
          </CollapsedContainer>
          {expandedRecommendations.includes(recommendation) && (
            <ExpandedContainer>
              <p>
                <strong>Request: </strong>
                <em>{recommendation.requestMessage}</em>
              </p>
              <hr />
              <CancelRequestDialog
                onClick={(notifyApplicant: boolean) =>
                  onCancelRequest(recommendation, notifyApplicant)
                }
                loading={cancelRequestLoading}
                error={cancelRequestError}
              />
              <ul>
                <li>
                  <div className="side-by-side-flex">
                    <div>We sent the initial email on: </div>
                    {moment(recommendation.createdAt).format('LLL')}
                  </div>
                </li>
              </ul>
            </ExpandedContainer>
          )}
        </Request>
      ))}
      {recommendations.length === 0 && (
        <EmptyMessage>
          <em>There are no active IDVT requests.</em>
        </EmptyMessage>
      )}
    </>
  )
}

export default ActiveRequests
